import { getSupabase } from './client';
import type { Json } from './database.types';
import { toDataError } from './errors';
import { toJson } from './json';
import type { WorkspaceScope } from './workspace';

export interface AuditEntry {
  readonly id: number;
  readonly at: string;
  readonly actorEmail: string | null;
  readonly actorName: string | null;
  readonly action: string;
  readonly module: string | null;
  readonly targetId: string | null;
  readonly label: string | null;
  readonly before: Json | null;
  readonly after: Json | null;
}

/** Before/after snapshots can contain Base64 attachments; keep the log lightweight. */
const MAX_SNAPSHOT_CHARS = 20_000;

function trimSnapshot(value: unknown): Json | null {
  if (value === undefined || value === null) return null;
  const json = toJson(value);
  const size = JSON.stringify(json).length;
  return size > MAX_SNAPSHOT_CHARS ? { truncated: true, size } : json;
}

function textOrNull(value: unknown, max = 500): string | null {
  if (value === undefined || value === null || value === '') return null;
  const text = typeof value === 'string' ? value : JSON.stringify(value);
  return text.length > max ? `${text.slice(0, max)}…` : text;
}

export interface AuditInput {
  action: string;
  module?: unknown;
  targetId?: unknown;
  label?: unknown;
  before?: unknown;
  after?: unknown;
  actorEmail?: string | null;
  actorName?: string | null;
}

export async function insertAudit(scope: WorkspaceScope, input: AuditInput): Promise<number> {
  const { data, error } = await getSupabase()
    .from('audit_log')
    .insert({
      workspace_id: scope.workspaceId,
      app_key: scope.appKey,
      action: input.action.slice(0, 60),
      module: textOrNull(input.module, 80),
      target_id: textOrNull(input.targetId),
      label: textOrNull(input.label),
      before: trimSnapshot(input.before),
      after: trimSnapshot(input.after),
      actor_email: input.actorEmail ?? null,
      actor_name: input.actorName ?? null,
    })
    .select('id')
    .single();
  if (error) throw toDataError(error);
  return data.id;
}

/** Admins and managers only (RLS); other roles get an empty list. */
export async function fetchAudit(scope: WorkspaceScope, limit = 800): Promise<AuditEntry[]> {
  const { data, error } = await getSupabase()
    .from('audit_log')
    .select('*')
    .eq('workspace_id', scope.workspaceId)
    .eq('app_key', scope.appKey)
    .order('at', { ascending: false })
    .limit(limit);
  if (error) throw toDataError(error);
  return (data ?? []).map((row) => ({
    id: row.id,
    at: new Date(row.at).toISOString(),
    actorEmail: row.actor_email,
    actorName: row.actor_name,
    action: row.action,
    module: row.module,
    targetId: row.target_id,
    label: row.label,
    before: row.before,
    after: row.after,
  }));
}

/** Admins only (RLS). */
export async function deleteAudit(scope: WorkspaceScope, ids: number[]): Promise<void> {
  if (!ids.length) return;
  const { error } = await getSupabase()
    .from('audit_log')
    .delete()
    .eq('workspace_id', scope.workspaceId)
    .eq('app_key', scope.appKey)
    .in('id', ids);
  if (error) throw toDataError(error);
}
